import { useEffect } from "react";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { AlertCircle } from "lucide-react";
import { useBranding } from "@/hooks/use-branding";

/**
 * Accessible Not Found page (WCAG 2.4.2 / 2.4.4): meaningful document title,
 * a real h1, a plain-language explanation, and a keyboard-accessible route
 * back to a safe page. Rendered for unknown routes both inside the admin
 * shell and (via the unauthenticated catch-all) for anonymous visitors.
 */
export default function NotFound() {
  const branding = useBranding();
  useEffect(() => {
    document.title = `Page not found — ${branding.productName}`;
  }, [branding.productName]);
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background">
      <Card className="w-full max-w-md mx-4">
        <CardContent className="pt-6">
          <div className="flex mb-4 gap-2 items-center">
            <AlertCircle className="h-8 w-8 text-destructive" aria-hidden="true" />
            <h1 className="text-2xl font-bold text-foreground" data-testid="text-404-title">
              Page not found
            </h1>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            The page you are looking for doesn't exist, may have moved, the
            address was mistyped — or your account may not have permission to
            view it. If you expected access, contact your administrator.
          </p>
          <p className="mt-4 text-sm">
            <Link href="/" className="font-medium text-accent underline underline-offset-2" data-testid="link-404-home">
              Go to the home page
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
